import React, { useCallback, useEffect, useState } from 'react';
import { FolderOpen, Play, MessageCircle, UserSearch, Settings2 } from 'lucide-react';
import AlbumManager from '../components/AlbumManager';
import PhotoSlideshow from '../components/PhotoSlideshow';
import { Link, useSearchParams } from 'react-router-dom';
import AccessGate from '../components/AccessGate';
import PhotoUploader from '../components/PhotoUploader';
import PhotoLightbox from '../components/PhotoLightbox';
import PhotoFrame from '../components/PhotoFrame';
import { useAuth } from '../context/AuthContext';

const PAGE_KEY = 'members-photos';

export default function MembersPhotos() {
  const { getPhotoGallery, listPhotoAlbums, mediaUrl, user, isAdmin } = useAuth();
  const [manage, setManage] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [show, setShow] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [tag, setTag] = useState('');
  const [year, setYear] = useState('');
  const [album, setAlbum] = useState('');

  const onCommentCount = useCallback((pid, n) => setItems((it) => it.map((p) => (p.id === pid ? { ...p, comment_count: n } : p))), []);
  const load = useCallback(() => {
    setLoading(true); setErr('');
    getPhotoGallery().then(setItems).catch(() => setErr('Could not load the gallery. Please try again.')).finally(() => setLoading(false));
  }, [getPhotoGallery]);
  const loadAlbums = useCallback(() => listPhotoAlbums().then(setAlbums).catch(() => setAlbums([])), [listPhotoAlbums]);
  useEffect(() => { load(); loadAlbums(); }, [load, loadAlbums]);

  const [params, setParams] = useSearchParams();
  const person = params.get('person') || '';
  const setPerson = (v) => setParams((prev) => { const n = new URLSearchParams(prev); if (v) n.set('person', v); else n.delete('person'); return n; }, { replace: true });
  const splitPeople = (p) => (p.people || '').split(',').map((x) => x.trim()).filter(Boolean);
  const allPeople = Array.from(new Set(items.flatMap(splitPeople))).sort((a, b) => a.localeCompare(b));
  const pl = person.trim().toLowerCase();
  const allTags = Array.from(new Set(items.flatMap((p) => p.tags || []))).sort();
  const allYears = Array.from(new Set(items.map((p) => (p.date_taken || '').slice(0, 4)).filter(Boolean))).sort().reverse();
  const albumsOf = (p) => (p.albums && p.albums.length ? p.albums : (p.album ? [p.album] : []));
  const allAlbums = Array.from(new Set(items.flatMap(albumsOf))).sort();
  const ql = q.trim().toLowerCase();
  const filtered = items.filter((p) => {
    if (tag && !(p.tags || []).includes(tag)) return false;
    if (year && (p.date_taken || '').slice(0, 4) !== year) return false;
    if (album && !albumsOf(p).includes(album)) return false;
    if (pl && !splitPeople(p).some((n) => n.toLowerCase().includes(pl))) return false;
    if (ql) {
      const hay = [p.title, p.description, p.place, p.people, p.member_name, (p.tags || []).join(' '), albumsOf(p).join(' ')].join(' ').toLowerCase();
      if (!hay.includes(ql)) return false;
    }
    return true;
  });
  const albumKey = (p) => `${String((p.album_pos && p.album_pos[album]) ?? (p.album === album ? p.album_order : null) ?? 99999).padStart(5, '0')}|${p.taken_at || p.date_taken || ''}`;
  const gridItems = album ? [...filtered].sort((a, b) => (albumKey(a) < albumKey(b) ? -1 : albumKey(a) > albumKey(b) ? 1 : 0)) : filtered;
  const hasFilters = q || tag || year || album || person;
  const clearFilters = () => { setQ(''); setTag(''); setYear(''); setAlbum(''); setPerson(''); };

  return (
    <AccessGate pageKey={PAGE_KEY}>
      <div className="max-w-5xl mx-auto px-4 py-10 fade-in">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-2">
          <div>
            <h1 className="font-title text-3xl md:text-4xl" style={{ color: 'var(--maroon)' }}>Members&apos; Photos</h1>
            <div className="h-1 w-20 mt-2" style={{ background: 'var(--gold)' }} />
          </div>
          <div className="flex items-center gap-2">
          {items.length > 0 && (
            <button onClick={() => setShow(true)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded text-sm font-title border" style={{ borderColor: 'var(--maroon)', color: 'var(--maroon)', background: 'white' }} data-testid="gallery-slideshow">
              <Play size={14} /> Slideshow{hasFilters ? ` (${gridItems.length})` : ''}
            </button>
          )}
          {user && (
            <button onClick={() => setShowUpload((s) => !s)} className="px-4 py-2 rounded text-sm font-title"
              style={{ background: showUpload ? 'white' : 'var(--maroon)', color: showUpload ? 'var(--maroon)' : 'var(--cream)', border: '1px solid var(--maroon)' }}
              data-testid="toggle-upload">
              {showUpload ? 'Close' : 'Share a photo'}
            </button>
          )}
          </div>
        </div>
        <p className="mb-6 text-[15px]" style={{ color: 'var(--ink)' }}>
          Photos shared by our members. Add your own from your Sapper days &mdash; each submission is reviewed before it appears here.
        </p>

        {showUpload && (
          <div className="mb-8 p-4 rounded-lg bg-white shadow-sm border" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
            <PhotoUploader onDone={() => { setShowUpload(false); load(); }} />
          </div>
        )}

        {!loading && !err && albums.length === 0 && isAdmin && !hasFilters && (
          <div className="mb-6"><button onClick={() => setManage(true)} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded border" style={{ borderColor: 'var(--navy)', color: 'var(--navy)', background: 'white' }} data-testid="gallery-manage-albums"><Settings2 size={12} /> Manage albums</button></div>
        )}
        {manage && <AlbumManager onClose={() => setManage(false)} onChanged={() => { loadAlbums(); load(); }} />}
        {!loading && !err && albums.length > 0 && !hasFilters && (
          <section className="mb-8" data-testid="gallery-albums">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-title text-lg flex items-center gap-2" style={{ color: 'var(--navy)' }}><FolderOpen size={18} /> Albums</h2>
              {isAdmin && <button onClick={() => setManage(true)} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded border" style={{ borderColor: 'var(--navy)', color: 'var(--navy)', background: 'white' }} data-testid="gallery-manage-albums"><Settings2 size={12} /> Manage albums</button>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {albums.map((a) => (
                <Link key={a.name} to={`/members/photos/album/${encodeURIComponent(a.name)}`} className="group block rounded-lg overflow-hidden shadow-sm bg-white" style={{ border: '1px solid rgba(0,0,0,0.08)' }} data-testid={`album-card-${a.name}`}>
                  <PhotoFrame src={mediaUrl(a.cover_url)} display={a.cover_display} alt={a.name} className="w-full h-36" style={{ background: 'rgba(0,0,0,0.04)' }} imgClassName="transition-transform duration-300 group-hover:scale-105" />
                  <div className="px-3 py-2">
                    <div className="font-title text-sm truncate" style={{ color: 'var(--maroon)' }}>{a.name}</div>
                    <div className="text-xs" style={{ color: 'var(--olive)' }}>{a.count} photo{a.count !== 1 ? 's' : ''}{a.latest ? ` · ${String(a.latest).slice(0, 4)}` : ''}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {!loading && !err && items.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="gallery-filters">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search photos…"
              className="px-3 py-1.5 rounded border text-sm flex-1 min-w-[160px]" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid="gallery-search" />
            {allPeople.length > 0 && (
              <div className="relative inline-flex items-center">
                <UserSearch size={14} className="absolute left-2.5 pointer-events-none" style={{ color: 'var(--olive)' }} />
                <input value={person} onChange={(e) => setPerson(e.target.value)} list="gallery-people-options" placeholder="Person's name…"
                  className="pl-8 pr-3 py-1.5 rounded border text-sm w-44" style={{ borderColor: person ? 'var(--maroon)' : 'rgba(0,0,0,0.2)' }} data-testid="gallery-person" />
                <datalist id="gallery-people-options">{allPeople.map((n) => <option key={n} value={n} />)}</datalist>
              </div>
            )}
            {allAlbums.length > 0 && (
              <select value={album} onChange={(e) => setAlbum(e.target.value)} className="px-2 py-1.5 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid="gallery-album">
                <option value="">All albums</option>
                {allAlbums.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            )}
            {allYears.length > 0 && (
              <select value={year} onChange={(e) => setYear(e.target.value)} className="px-2 py-1.5 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid="gallery-year">
                <option value="">All years</option>
                {allYears.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
            {allTags.length > 0 && (
              <select value={tag} onChange={(e) => setTag(e.target.value)} className="px-2 py-1.5 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid="gallery-tag">
                <option value="">All tags</option>
                {allTags.map((t) => <option key={t} value={t}>{`#${t}`}</option>)}
              </select>
            )}
            {hasFilters && (
              <button onClick={clearFilters} className="px-3 py-1.5 rounded text-sm" style={{ border: '1px solid rgba(0,0,0,0.2)', color: 'var(--maroon)' }} data-testid="gallery-clear-filters">Clear</button>
            )}
          </div>
        )}

        {person && !loading && (
          <h2 className="font-title text-lg mb-3 inline-flex items-center gap-2" style={{ color: 'var(--navy)' }} data-testid="gallery-person-heading">
            <UserSearch size={18} /> Photos featuring “{person}” <span className="text-sm font-normal" style={{ color: 'var(--olive)' }}>({gridItems.length})</span>
          </h2>
        )}
        {loading ? (
          <p className="text-sm" style={{ color: 'var(--olive)' }}>Loading photos…</p>
        ) : err ? (
          <p className="text-sm px-3 py-2 rounded" style={{ background: '#fdecea', color: '#8a1f11' }} data-testid="gallery-error">{err}</p>
        ) : items.length === 0 ? (
          <div className="text-center py-16 rounded-lg" style={{ background: 'rgba(0,0,0,0.03)' }} data-testid="gallery-empty">
            <p className="text-sm" style={{ color: 'var(--olive)' }}>No member photos yet. Be the first to share one!</p>
          </div>
        ) : gridItems.length === 0 ? (
          <div className="text-center py-12 rounded-lg" style={{ background: 'rgba(0,0,0,0.03)' }} data-testid="gallery-no-match">
            <p className="text-sm" style={{ color: 'var(--olive)' }}>No photos match your filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="member-gallery-grid">
            {gridItems.map((p, i) => (
              <button key={p.id} onClick={() => setLightbox(i)} className="group relative overflow-hidden rounded shadow-sm" style={{ border: '3px solid white' }} data-testid={`gallery-item-${i}`}>
                <PhotoFrame src={mediaUrl(p.url)} display={p.display} alt={p.title} className="w-full h-44" style={{ background: 'rgba(0,0,0,0.04)' }} imgClassName="transition-transform duration-300 group-hover:scale-105" />
                <span className="absolute inset-x-0 bottom-0 px-2 py-1.5 text-xs text-left opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: 'rgba(21,56,91,0.85)', color: 'var(--cream)' }}>
                  {p.title || p.member_name}
                </span>
                {p.comment_count > 0 && <span className="absolute top-2 right-2 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(21,56,91,0.85)', color: 'var(--cream)' }} data-testid={`gallery-comment-count-${p.id}`}><MessageCircle size={10} /> {p.comment_count}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      {show && gridItems.length > 0 && <PhotoSlideshow photos={gridItems} title={album ? `Album: ${album}` : "Members' Photos"} mediaUrl={mediaUrl} onClose={() => setShow(false)} />}
      <PhotoLightbox items={gridItems} index={lightbox} mediaUrl={mediaUrl} onCommentCount={onCommentCount}
        onClose={() => setLightbox(null)}
        onPrev={() => setLightbox((i) => (i - 1 + gridItems.length) % gridItems.length)}
        onNext={() => setLightbox((i) => (i + 1) % gridItems.length)} />
    </AccessGate>
  );
}
